import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import endpoints from "../../endpoint";
import { Upload } from "tus-js-client";

// Async Thunks
export const fetchVideos = createAsyncThunk(
  "video/fetchVideos",
  async (courseId, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${endpoints.serverBaseUrl}api/video/${courseId}/video`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadVideo = createAsyncThunk(
  "video/uploadVideo",
  async (
    {
      title,
      duration,
      file,
      thumbnail,
      courseId,
      techId,
      description,
      onProgress,
    },
    { rejectWithValue }
  ) => {
    const token = sessionStorage.getItem("token");
    let videoId = "";

    try {
      console.log("Uploading video with duration:", duration);

      // Ensure video duration is available
      if (!duration) {
        try {
          duration = await getVideoDuration(file);
          console.log("Extracted video duration:", duration);
        } catch (error) {
          console.error("Error extracting video duration:", error);
          return rejectWithValue("Failed to extract video duration.");
        }
      }

      // ✅ Wrap the video upload in a Promise to wait for completion
      const uploadResult = await new Promise((resolve, reject) => {
        const videoUpload = new Upload(file, {
          endpoint: `${endpoints.serverBaseUrl}api/generate-upload-url`,
          retryDelays: [0, 3000, 5000, 10000],
          chunkSize: 15 * 1024 * 1024,
          metadata: { name: file.name },
          onError: (error) => {
            console.error("Upload Error:", error);
            reject("Video upload failed due to network or server error.");
          },
          onSuccess: async () => {
            try {
              console.log("Video upload successful!");
              const uploadURL = videoUpload.url;
              videoId = uploadURL.split("/tus/")[1].split("?")[0];
              resolve(videoId);
            } catch (error) {
              console.error("Error parsing video upload response:", error);
              reject("Failed to retrieve video ID from upload response.");
            }
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const progress = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            console.log("Video upload progress:", progress);

            if (onProgress) {
              onProgress(progress);
            }
          },
        });

        videoUpload.start();
      });

      if (!uploadResult) {
        return rejectWithValue("Video upload did not complete successfully.");
      }

      let thumbnailPublicId = "";

      try {
        //  Handle thumbnail upload
        const thumbnailResponse = await axios.post(
          `${endpoints.serverBaseUrl}api/generate-presigned-url`,
          { fileName: thumbnail.name, fileType: thumbnail.type },
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        const { presignedUrl, publicId } = thumbnailResponse.data;
        thumbnailPublicId = publicId;

        await axios.put(presignedUrl, thumbnail, {
          headers: { "Content-Type": thumbnail.type },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percent);
            }
          },
        });
      } catch (error) {
        console.error("Thumbnail upload error:", error);
        return rejectWithValue("Thumbnail upload failed.");
      }

      try {
        const videoData = {
          title,
          description,
          duration: duration || 0,
          teacherId: techId,
          courseId,
          publicId: videoId,
          publicImageId: thumbnailPublicId,
        };

        const response = await axios.post(
          `${endpoints.serverBaseUrl}api/video/${courseId}/video`,
          videoData,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        console.log("Video upload response:", response.data);
        return response.data.data;
      } catch (error) {
        console.error("Error saving video metadata:", error);
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to save video metadata in backend."
        );
      }
    } catch (error) {
      console.error("Unexpected error in uploadVideo:", error);
      return rejectWithValue(
        "An unexpected error occurred while uploading the video."
      );
    }
  }
);

// Function to get video duration
const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const videoElement = document.createElement("video");
    videoElement.preload = "metadata";

    videoElement.onloadedmetadata = () => {
      resolve(videoElement.duration);
    };

    videoElement.onerror = (error) => {
      reject("Error extracting video duration");
    };

    const url = URL.createObjectURL(file);
    videoElement.src = url;
  });
};

//this clodnary and cloudflare r2 same for both

export const deleteVideo = createAsyncThunk(
  "video/deleteVideo",
  async (
    { courseId, videoId, publicId, publicImageId },
    { rejectWithValue }
  ) => {
    const token = sessionStorage.getItem("token");

    try {
      const response = await axios.delete(
        `${endpoints.serverBaseUrl}api/video/${courseId}/video`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            publicId,
            publicImageId,
          },
        }
      );
      return { videoId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateVideo = createAsyncThunk(
  "video/updateVideo",
  async (
    {
      courseId,
      NewThumbnail,
      publicImageId,
      publicId,
      videoId,
      title,
      description,
    },
    { rejectWithValue }
  ) => {
    const token = sessionStorage.getItem("token");
    let thumbnailPublicId = "";

    if (NewThumbnail) {
      await axios.post(
        `${endpoints.serverBaseUrl}api/course/media/delete`,
        { deletePayload: { imagePublicId: publicImageId } },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
    }

    try {
      //  Handle thumbnail upload
      const thumbnailResponse = await axios.post(
        `${endpoints.serverBaseUrl}api/generate-presigned-url`,
        { fileName: NewThumbnail.name, fileType: NewThumbnail.type },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      const { presignedUrl, publicId } = thumbnailResponse.data;
      thumbnailPublicId = publicId;

      await axios.put(presignedUrl, NewThumbnail, {
        headers: { "Content-Type": NewThumbnail.type },
      });
    } catch (error) {
      console.error("Thumbnail upload error:", error);
      return rejectWithValue("Thumbnail upload failed.");
    }

    try {
      const response = await axios.put(
        `${endpoints.serverBaseUrl}api/video/${courseId}/video`,
        {
          publicId,
          publicImageId: thumbnailPublicId,
          title,
          description,
          videoId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { videoId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);




const initialState = {
  videos: [],
  loading: false,
  error: null,
  uploadStatus: "idle",
  updateStatus: null,
};

// Create the Slice
const videoUploadFetch = createSlice({
  name: "video",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Videos
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = action.payload;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Upload Video
    builder
      .addCase(uploadVideo.pending, (state) => {
        state.uploadStatus = "uploading";
        state.error = null;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.uploadStatus = "success";
        state.videos.push(action.payload);
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.uploadStatus = "failed";
        state.error = action.payload;
      });
    // delete video
    builder
      .addCase(deleteVideo.pending, (state) => {
        state.deleteStatus = "deleting";
        state.error = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.deleteStatus = "success";
        // Remove the video from the state
        state.videos = state.videos.filter(
          (video) => video._id !== action.payload.videoId
        );
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload;
      });
    // update video
    builder
      .addCase(updateVideo.pending, (state, action) => {
        const { videoId, title, description } = action.meta.arg;
        const index = state.videos.findIndex((video) => video._id === videoId);
        if (index !== -1) {
          state.videos[index] = { ...state.videos[index], title, description };
        }
      })
      .addCase(updateVideo.fulfilled, (state, action) => {
        state.updateStatus = "success";

        // Update the specific video in the state
        const updatedVideoIndex = state.videos.findIndex(
          (video) => video._id === action.payload.videoId
        );
        if (updatedVideoIndex !== -1) {
          state.videos[updatedVideoIndex] = {
            ...state.videos[updatedVideoIndex],
            ...action.payload.updatedVideo,
          };
        }
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload;
      });
  },
});

export default videoUploadFetch.reducer;
